import { RevenueGranularity } from '@navio/nsdk';
import { addDays, differenceInCalendarDays, startOfMonth, startOfYear, subDays, subMonths, subYears } from 'date-fns';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';

export const REVENUE_RANGES = ['7d', '30d', '13w', 'ytd', '12m'] as const;
export type RevenueRange = (typeof REVENUE_RANGES)[number];

export const REVENUE_COMPARISONS = ['previous', 'year'] as const;
export type RevenueComparison = (typeof REVENUE_COMPARISONS)[number];

interface RangeSpec {
  label: string;
  granularity: RevenueGranularity;
  /** Days of booked work fetched past today, shown when upcoming bookings are included. */
  horizonDays: number;
  firstDay: (today: Date) => Date;
}

const RANGE_SPECS: Record<RevenueRange, RangeSpec> = {
  '7d': {
    label: '7 days',
    granularity: RevenueGranularity.DAY,
    horizonDays: 7,
    firstDay: (today) => subDays(today, 6)
  },
  '30d': {
    label: '30 days',
    granularity: RevenueGranularity.DAY,
    horizonDays: 14,
    firstDay: (today) => subDays(today, 29)
  },
  '13w': {
    label: '13 weeks',
    granularity: RevenueGranularity.WEEK,
    horizonDays: 28,
    firstDay: (today) => subDays(today, 90)
  },
  ytd: { label: 'Year to date', granularity: RevenueGranularity.MONTH, horizonDays: 59, firstDay: startOfYear },
  '12m': {
    label: '12 months',
    granularity: RevenueGranularity.MONTH,
    horizonDays: 59,
    firstDay: (today) => startOfMonth(subMonths(today, 11))
  }
};

export const revenueRangeOptions = REVENUE_RANGES.map((range) => ({ value: range, label: RANGE_SPECS[range].label }));

export interface RevenueQueryWindow {
  granularity: RevenueGranularity;
  fromDate: string;
  /** Includes the range's horizon of booked work. */
  toDate: string;
  comparisonFromDate: string;
  comparisonToDate: string;
  /** Monthly ranges always compare with last year: the previous period would be a different season. */
  comparison: RevenueComparison;
}

/**
 * The server buckets both windows the same way and the page pairs periods by position, so the comparison window is
 * shifted by whole weeks (or a whole year for months) to keep each period's weekday mix.
 */
export const getRevenueQueryWindow = ({
  range,
  comparison,
  today
}: {
  range: RevenueRange;
  comparison: RevenueComparison;
  today: Date;
}): RevenueQueryWindow => {
  const spec = RANGE_SPECS[range];
  const from = spec.firstDay(today);
  const to = addDays(today, spec.horizonDays);
  const isMonthly = spec.granularity === RevenueGranularity.MONTH;
  const effectiveComparison: RevenueComparison = isMonthly ? 'year' : comparison;
  const shift = (date: Date) => {
    if (isMonthly) {
      return subYears(date, 1);
    }
    return subDays(date, effectiveComparison === 'year' ? 364 : differenceInCalendarDays(today, from) + 1);
  };
  return {
    granularity: spec.granularity,
    fromDate: toApiDate(from),
    toDate: toApiDate(to),
    comparisonFromDate: toApiDate(shift(from)),
    comparisonToDate: toApiDate(shift(to)),
    comparison: effectiveComparison
  };
};

const toApiDate = (date: Date) => noddiFormatDate(date, NoddiDateFormats.API_DATE);
